// File: InterviewResult.jsx
import React, { useEffect, useState } from"react";
import { useLocation, useNavigate } from"react-router-dom";
import { useTenant } from"../../Context/TenantContext";
import API_BASE_URL from"../../Config/config";

export default function InterviewResult({ mode = "learner" }) {
 const { state } = useLocation();
 const navigate = useNavigate();
 const { getCollegeName, getLogoUrl, tenant } = useTenant();
 const [attemptRecorded, setAttemptRecorded] = useState(false);

 const isTenantMode = mode === "tenant" || !!tenant;
 const collegeName = getCollegeName();
 const collegeLogo = getLogoUrl();

 // Record mock interview attempt when result is viewed
 useEffect(() => {
   if (!state) return;

   const recordAttempt = async () => {
     if (attemptRecorded) return;

     try {
       const userRaw = localStorage.getItem("user");
       if (!userRaw) return;

       const user = JSON.parse(userRaw);
       const userId = user?.userId || user?.user_id;
       if (!userId) return;

       // Map interview types to standard format
       const typeMap = {
         'HR': 'HR',
         'Technical': 'Technical',
         'Coding': 'AI Coding'
       };
       const mappedType = typeMap[state.interviewType] || state.interviewType;

       const response = await fetch(`${API_BASE_URL}/api/usage/mock-interview`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'user-id': userId,
           'x-tenant-id': tenant || ''
         },
         body: JSON.stringify({
           user_id: userId,
           interview_type: mappedType
         })
       });

       if (response.ok) {
         setAttemptRecorded(true);
         console.log('Mock interview attempt recorded successfully');
         // Store in local storage as fallback for access control
         try {
             const currentCount = Number(localStorage.getItem('mock_interview_usage_count') || 0);
             localStorage.setItem('mock_interview_usage_count', String(currentCount + 1));
         } catch (e) {}
       }
     } catch (error) {
       console.error('Failed to record mock interview attempt:', error);
     }
   };

   recordAttempt();
 }, [state, attemptRecorded]);

 if (!state) return <p className="p-6 text-white">No result data found.</p>;

 const {
 score,
 strengths,
 improvements,
 detailed,
 time,
 total,
 answers,
 questions,
 interviewType,
 } = state;

 return (
 <div className="min-h-screen bg-bgMain text-body p-6">
 {isTenantMode && collegeLogo && (
   <div className="flex justify-center mb-8">
     <img src={collegeLogo} alt={collegeName} className="h-16 object-contain" />
   </div>
 )}
 <h1 className="text-3xl mb-4 text-primary font-bold">
 {isTenantMode ? `${collegeName} Interview Result` : (interviewType ==="HR" ?"HR Interview Result" :"Technical Interview Result")}
 </h1>

 <p className="mb-2">
 Score: <span className="text-green-400 font-bold">{Number.isFinite(score) ? Math.round(score) : 0}%</span>
 </p>
 <p className="mb-2">
 Total Time: <span className="text-blue-400 font-mono">{time}</span>
 </p>
 <p className="mb-6">Total Questions: {total}</p>

 {strengths?.length > 0 && (
 <>
 <h2 className="text-xl mt-6 mb-2 text-primary">Strengths</h2>
 <ul className="list-disc pl-6 text-green-300">
 {strengths.map((s, idx) => (
 <li key={idx}>{s}</li>
 ))}
 </ul>
 </>
 )}

 {improvements?.length > 0 && (
 <>
 <h2 className="text-xl mt-6 mb-2 text-primary">Areas to Improve</h2>
 <ul className="list-disc pl-6 text-red-400">
 {improvements.map((s, idx) => (
 <li key={idx}>{s}</li>
 ))}
 </ul>
 </>
 )}

 {detailed && (
 <>
 <h2 className="text-xl mt-6 mb-2 text-primary">Detailed Feedback</h2>
 <p className="text-muted mb-6">{detailed}</p>
 </>
 )}

 <h2 className="text-xl mb-2 text-primary">Question Review</h2>
 {(questions || []).map((q, idx) => {
 const answer = (answers || []).find(a => a.question === q.question);
 const userAnswer = answer && answer.answer && String(answer.answer).trim() ? answer.answer : null;
 const correctAnswer = q.correct_answer || q.expected_answer || q.sample_answer;
 const isCorrect = correctAnswer && userAnswer && 
 String(userAnswer).toLowerCase().trim() === String(correctAnswer).toLowerCase().trim();
 
 return (
 <div key={idx} className="mb-6 p-4 bg-[#1a1a1a] rounded-lg border border-gray-700">
 <p className="text-primary font-semibold mb-2">
 Q{idx + 1}: {q.question}
 </p>
 
 <div className="space-y-2">
 <div>
 <span className="text-muted">Your Answer: </span>
 <span className={isCorrect ?"text-green-400 font-semibold" :"text-red-400 font-semibold"}>
 {userAnswer || <em className="text-red-400">Skipped</em>}
 </span>
 {isCorrect && <span className="ml-2 text-green-400">✅</span>}
 {!isCorrect && userAnswer && <span className="ml-2 text-red-400">❌</span>}
 </div>
 
 {correctAnswer && (
 <div>
 <span className="text-muted">Correct Answer: </span>
 <span className="text-green-400 font-semibold">{correctAnswer}</span>
 </div>
 )}
 
 {q.guidance && (
 <div className="mt-3 p-3 bg-blue-900/30 border border-blue-500/50 rounded">
 <span className="text-blue-300 font-semibold">💡 Guidance: </span>
 <span className="text-gray-200">{q.guidance}</span>
 </div>
 )}
 
 {q.explanation && (
 <div className="mt-2 p-3 bg-purple-900/30 border border-purple-500/50 rounded">
 <span className="text-purple-300 font-semibold">📚 Explanation: </span>
 <span className="text-gray-200">{q.explanation}</span>
 </div>
 )}
 </div>
 </div>
 );
 })}

 <button
 className="mt-6 px-4 py-2 bg-secondary hover:bg-primary text-white font-bold rounded"
 onClick={() => navigate(isTenantMode ? "/student/mockinterviewhome" : "/mockinterviewhome")}
 >
 Start New Interview
 </button>
 </div>
 );
}



